import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const GiveawayContent = ({ title, description, prize, instagramUsername, onInstagramChange, onParticipate }) => {
  const [hasParticipated, setHasParticipated] = useState(false);
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleParticipate = async () => {
    if (!instagramUsername?.trim() || isLoading) return;
    setIsLoading(true);
    setIsDuplicate(false);
    try {
      const result = onParticipate ? await onParticipate() : null;
      if (result?.error?.message === 'Already participated') {
        setIsDuplicate(true);
      } else {
        setHasParticipated(true);
      }
    } catch (e) {
      console.error('Participation error:', e);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-xl flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-accent to-warning">
          <Icon name="Gift" size={24} color="white" />
        </div>
        <div className="flex-1">
          <h3 className="text-xl md:text-2xl lg:text-3xl font-heading font-semibold text-foreground mb-3 md:mb-4">
            {title || 'Çekiliş Günü!'}
          </h3>
          <p className="text-base md:text-lg lg:text-xl text-foreground leading-relaxed mb-4 md:mb-5">
            {description || 'Bugün şanslı gününüz olabilir! Çekilişe katılın ve harika ödüller kazanma şansını yakalayın.'}
          </p>
          <div className="p-6 md:p-8 rounded-xl bg-gradient-to-br from-primary/10 to-accent/10 border-2 border-primary/20">
            <div className="flex items-center gap-3 mb-3">
              <Icon name="Trophy" size={24} color="var(--color-primary)" />
              <h4 className="text-lg md:text-xl font-heading font-semibold text-foreground">
                Ödül
              </h4>
            </div>
            <p className="text-base md:text-lg text-foreground font-medium">
              {prize || '1 Yıllık Premium Üyelik + Özel Araç Kokusu Seti'}
            </p>
          </div>
        </div>
      </div>

      {/* Instagram Username - Required for Giveaway */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 flex items-center justify-center flex-shrink-0">
            <Icon name="Instagram" size={20} color="white" />
          </div>
          <div>
            <h4 className="text-base md:text-lg font-heading font-semibold text-foreground">
              Instagram Kullanıcı Adı
            </h4>
            <p className="text-xs md:text-sm text-muted-foreground">
              Çekilişe katılmak için Instagram kullanıcı adını gir <span className="text-destructive font-medium">*</span>
            </p>
          </div>
        </div>
        <Input
          type="text"
          placeholder="@kullaniciadi"
          value={instagramUsername || ''}
          onChange={(e) => onInstagramChange?.(e?.target?.value)}
          disabled={hasParticipated || isDuplicate}
          className="text-base md:text-lg"
        />
        {instagramUsername?.trim() && !isDuplicate && (
          <p className="text-xs md:text-sm text-success flex items-center gap-2">
            <Icon name="CheckCircle" size={16} />
            Kullanıcı adı girildi
          </p>
        )}
        {!instagramUsername?.trim() && (
          <p className="text-xs md:text-sm text-destructive flex items-center gap-2">
            <Icon name="AlertCircle" size={16} />
            Çekilişe katılmak için Instagram kullanıcı adı zorunludur
          </p>
        )}
      </div>

      <div className="space-y-4">
        <Button
          variant={hasParticipated ? 'success' : 'default'}
          size="lg"
          fullWidth
          onClick={handleParticipate}
          disabled={hasParticipated || isDuplicate || !instagramUsername?.trim() || isLoading}
          iconName={hasParticipated ? 'CheckCircle' : 'Sparkles'}
          iconPosition="left"
          className="spring-animation"
        >
          {isLoading ? 'Kaydediliyor...' : hasParticipated ? 'Çekilişe Katıldın!' : 'Çekilişe Katıl'}
        </Button>

        {hasParticipated && (
          <div className="p-4 md:p-5 rounded-lg bg-success/10 border border-success/20">
            <p className="text-sm md:text-base text-center flex items-center justify-center gap-2 text-[rgba(16,203,6,1)]">
              <Icon name="PartyPopper" size={18} />
              Katılımın başarıyla kaydedildi! Sonuçlar yakında açıklanacak.
            </p>
          </div>
        )}

        {isDuplicate && (
          <div className="p-4 md:p-5 rounded-lg bg-destructive/10 border border-destructive/20">
            <p className="text-sm md:text-base text-center flex items-center justify-center gap-2 text-destructive">
              <Icon name="AlertCircle" size={18} />
              Bu Instagram kullanıcı adı ile daha önce çekilişe katılındı. Her kullanıcı adı yalnızca bir kez katılabilir.
            </p>
          </div>
        )}

        <p className="text-xs md:text-sm text-center text-muted-foreground">
          Çekiliş tarihi: {new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)?.toLocaleDateString('tr-TR')}
        </p>
      </div>
    </div>
  );
};

export default GiveawayContent;